import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { agentMetricsColumns } from '../../agent-performance/tableConfig';
import axiosInstance from '@/lib/axios/config/axiosConfig';
import { PaginatedResponse } from '@/lib/axios/types';
import { AgentMetricsData, convertApiToTableData } from '../../agent-performance/agentTableTypes';
import {
  AgentMetricsDetailRequest,
  NewAgentApiResponse,
  UseAgentMetricsTableProps,
  UseAgentMetricsTableReturn
} from '../types';



export const useAgentMetricsTable = ({
  limit = 20,
  searchTerm = "",
  dateParams
}: UseAgentMetricsTableProps = {}): UseAgentMetricsTableReturn => {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [pagedData, setPagedData] = useState<PaginatedResponse<AgentMetricsData> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const columns = useMemo(() => agentMetricsColumns, []);

  // Use ref to store the fetch function to avoid dependency cycles
  const fetchAgentsRef = useRef<((page: number) => Promise<void>) | null>(null);

  // Fetch agents from API
  const fetchAgents = useCallback(async (page: number = 1) => {
    if (!dateParams?.startDate || !dateParams?.endDate) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // cancel any in-flight request before starting a new one
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      const requestBody: AgentMetricsDetailRequest = {
        startDate: dateParams.startDate,
        endDate: dateParams.endDate,
        agentMicrosoftIds: dateParams.agentMicrosoftIds || null,
        searchTerm: searchTerm,
        pageNumber: page,
        pageSize: limit
      };

      const response = await axiosInstance.post<NewAgentApiResponse>(
        '/AgentMetricsDetail/detail',
        requestBody,
        { signal: controller.signal }
      );

      const apiResponse = response.data;

      const tableData = convertApiToTableData(apiResponse.data);

      const paginatedData: PaginatedResponse<AgentMetricsData> = {
        data: tableData,
        total: apiResponse.totalCount,
        page: page,
        limit: limit,
        totalPages: Math.ceil(apiResponse.totalCount / limit)
      };

      setPagedData(paginatedData);

    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') {
        return; // ignore aborted requests
      }
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      console.error('Error fetching agent data:', err);
    } finally {
      setIsLoading(false);
      // clear ref if this is the last controller
      if (abortRef.current?.signal?.aborted) {
        abortRef.current = null;
      }
    }
  }, [dateParams, searchTerm, limit]);

  // Update ref whenever fetchAgents changes
  fetchAgentsRef.current = fetchAgents;

  // Toggle row expansion
  const toggleRowExpansion = useCallback((agentId: number) => {
    setPagedData(prevData => {
      if (!prevData) return prevData;

      return {
        ...prevData,
        data: prevData.data.map(agent => {
          if (agent.id === agentId) {
            return { ...agent, isExpanded: !agent.isExpanded };
          }
          return agent;
        })
      };
    });
  }, []);

  // Reset current page and fetch data when dateParams change
  useEffect(() => {
    setCurrentPage(1);
    if (fetchAgentsRef.current) {
      fetchAgentsRef.current(1);
    }
    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [dateParams?.startDate, dateParams?.endDate, dateParams?.agentMicrosoftIds, dateParams?.timeStamp, searchTerm]);

  const processPageChange = useCallback((page: number) => {
    const totalPages = pagedData?.totalPages || 1;
    const newPage = page > currentPage ? Math.min(totalPages, page) : Math.max(1, page);
    setCurrentPage(newPage);
    if (fetchAgentsRef.current) {
      fetchAgentsRef.current(newPage);
    }
  }, [currentPage, pagedData?.totalPages]);

  // Modal handlers
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return {
    pagedData,
    columns,
    isLoading,
    error,
    currentPage,
    setCurrentPage,
    processPageChange,
    toggleRowExpansion,
    isModalOpen,
    openModal,
    closeModal
  };
};
